import { apply, celebrate, onceForever, toast } from '../core/effects';
import { S } from '../core/game';
import { m, type Messages } from '../i18n';
import { sfx } from '../audio/sfx';
import { say } from '../ui/dialog';
import { h } from '../ui/dom';
import { pickImage, snapshot } from '../ui/snapshot';
import { closePhone, openPhone, registerTab, render } from './phone';

type CaseKey = keyof Messages['social']['cases'];
type OldKey = keyof Messages['phone']['feed']['posts'];

/** Posts that teach online safety; each one is solved once. */
const CASES: { key: CaseKey; color: string }[] = [
  { key: 'fakeFriend', color: '#c58cff' },
  { key: 'privatePhoto', color: '#ff7a9c' },
  { key: 'romance', color: '#ff9f43' },
  { key: 'fakeNews', color: '#3a8fd9' },
  { key: 'phishing', color: '#e74c3c' },
  { key: 'easyMoney', color: '#27ae60' },
];
const OLD: { key: OldKey; color: string; scam: boolean }[] = [
  { key: 'scam1', color: '#e74c3c', scam: true },
  { key: 'scam2', color: '#f39c12', scam: true },
  { key: 'rain', color: '#2ecc71', scam: false },
];

/** UI state that must survive re-renders: open comment threads and unsent drafts. */
const open = new Set<string>();
const drafts = new Map<string, string>();
let composing: { img: string } | null = null;
let composeText = '';

function comments(key: string): { who: string; text: string }[] {
  const s = S();
  s.feed.comments ??= {};
  return (s.feed.comments[key] ??= []);
}

/** Friends answer what you actually wrote: the reply depends on what the comment is about. */
const INTENTS: [keyof Messages['social']['replies'], RegExp][] = [
  ['sad', /(sad|tired|cry|難過|难过|累|哭|triste|cansad|plor)/i],
  ['thanks', /(thank|thx|謝|谢|gràcies|gracias|merci)/i],
  ['love', /(love|❤|💖|愛|爱|喜歡|te quiero|t'estimo|estimo)/i],
  ['laugh', /(haha|lol|😂|🤣|哈哈|jaja|jeje|xd)/i],
  ['food', /(food|eat|yum|吃|好吃|餓|comida|comer|menjar|🍙|🍰|🍪)/i],
  ['plan', /(let'?s|together|weekend|一起|週末|周末|vamos|quedamos|anem|cap de setmana)/i],
  ['question', /(\?|？|嗎|吗|呢)/],
];
function friendReply(text: string, seed: number): string {
  const r = m().social.replies;
  const intent = INTENTS.find(([, re]) => re.test(text))?.[0] ?? 'generic';
  const pool = r[intent];
  return pool[seed % pool.length];
}

function addComment(key: string, text: string, ownPost: boolean): void {
  const t = m().social;
  const list = comments(key);
  list.push({ who: t.you, text });
  sfx('pop');
  render();
  // A friend answers a moment later (on your posts and on friends' posts).
  const names = t.friendNames;
  const friend = names[(list.length + key.length) % names.length];
  window.setTimeout(() => {
    list.push({ who: friend, text: friendReply(text, list.length + text.length) });
    if (ownPost) apply({ mood: 2 }, { float: false });
    sfx('pop');
    render();
  }, 1100);
}

function commentBox(key: string, ownPost: boolean): HTMLElement {
  const t = m().social;
  const area = h('textarea', {
    class: 'comment-input',
    rows: 1,
    maxlength: 280,
    placeholder: `${t.writeComment}…`,
    'data-key': `c:${key}`,
    'aria-label': t.writeComment,
  });
  area.value = drafts.get(key) ?? '';
  const send = () => {
    const v = area.value.trim();
    if (!v) return;
    drafts.delete(key);
    area.value = '';
    addComment(key, v, ownPost);
  };
  area.addEventListener('input', () => {
    drafts.set(key, area.value);
    area.style.height = 'auto';
    area.style.height = `${Math.min(90, area.scrollHeight)}px`;
  });
  area.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  });
  const thread = comments(key);
  return h(
    'div',
    { class: 'comments' },
    thread.map((c) =>
      h(
        'div',
        { class: `comment${c.who === t.you ? ' mine' : ''}` },
        h('b', null, c.who),
        h('span', null, c.text),
        c.who !== t.you
          ? h(
              'button',
              {
                class: 'reply-link',
                onclick: () => {
                  drafts.set(key, `@${c.who} `);
                  render();
                  document.querySelector<HTMLTextAreaElement>(`[data-key="c:${key}"]`)?.focus();
                },
              },
              '↩',
            )
          : null,
      ),
    ),
    h('div', { class: 'comment-row' }, area, h('button', { class: 'round green', 'aria-label': m().phone.send, onclick: send }, '➤')),
    h(
      'div',
      { class: 'quick-replies' },
      t.replyOptions.map((q) => h('button', { class: 'chip small', onclick: () => addComment(key, q, ownPost) }, q)),
    ),
  );
}

function actionsRow(key: string, likes: number, liked: boolean, ownPost: boolean, onLike: () => void): HTMLElement {
  const t = m().social;
  const n = comments(key).length;
  return h(
    'div',
    { class: 'fb-actions' },
    h('button', { class: `btn small ${liked ? 'red' : 'ghost'}`, onclick: onLike }, `${liked ? '❤️' : '🤍'} ${likes}`),
    h(
      'button',
      {
        class: 'btn small ghost',
        onclick: () => {
          if (open.has(key)) open.delete(key);
          else open.add(key);
          sfx('click');
          render();
        },
      },
      `💬 ${t.comments} ${n || ''}`,
    ),
    void ownPost,
  );
}

function myPost(p: ReturnType<typeof S>['posts'][number]): HTMLElement {
  const t = m().social;
  const key = `mine.${p.id}`;
  const caption = p.caption >= 0 ? t.captions[p.caption] : (drafts.get(`cap.${p.id}`) ?? '');
  return h(
    'article',
    { class: 'fb-post mine' },
    h('div', { class: 'fb-author' }, h('span', { class: 'av', style: 'background:#e67e22' }, '🙂'), h('div', null, h('b', null, t.you), h('small', null, m().phone.now))),
    p.img ? h('img', { class: 'post-img', src: p.img, alt: '' }) : null,
    h('p', null, (p as { text?: string }).text ?? caption),
    actionsRow(key, p.likes, true, true, () => {}),
    open.has(key) ? commentBox(key, true) : null,
  );
}

function compose(): HTMLElement | null {
  if (!composing) return null;
  const t = m().social;
  const area = h('textarea', { class: 'comment-input big', rows: 3, maxlength: 400, placeholder: t.captionTitle, 'data-key': 'compose' });
  area.value = composeText;
  area.addEventListener('input', () => (composeText = area.value));
  area.addEventListener('keydown', (e) => e.stopPropagation());
  const publish = (text: string, caption: number) => {
    const s = S();
    const id = Date.now();
    s.posts.push({ id, img: composing!.img, caption, likes: 0, comments: [], day: s.dayIndex, ...({ text } as object) } as never);
    s.posts = s.posts.slice(-12);
    composing = null;
    composeText = '';
    open.add(`mine.${id}`);
    if (onceForever('feed.firstPhoto')) {
      celebrate();
      apply({ stars: 3, mood: 6 });
    }
    toast(t.posted);
    render();
    // Likes and comments trickle in from friends.
    const post = () => s.posts.find((x) => x.id === id);
    const pool = caption >= 0 ? t.friendComments[caption] : ['✨', '😍', '👍'];
    [700, 1600, 2600, 4200, 6000].forEach((ms, i) =>
      window.setTimeout(() => {
        const p = post();
        if (!p) return;
        p.likes += 1 + (i % 2);
        if (i < pool.length) comments(`mine.${id}`).push({ who: t.friendNames[i % t.friendNames.length], text: pool[i] });
        sfx('pop');
        render();
      }, ms),
    );
  };
  return h(
    'div',
    { class: 'composer' },
    h('img', { class: 'post-img', src: composing.img, alt: '' }),
    h('div', { class: 'chips' }, t.captions.map((cap, i) => h('button', { class: 'chip small', onclick: () => publish(cap, i) }, cap))),
    area,
    h(
      'div',
      { class: 'grid2' },
      h('button', { class: 'btn ghost', onclick: () => ((composing = null), render()) }, m().common.cancel),
      h('button', { class: 'btn green', onclick: () => composeText.trim() && publish(composeText.trim(), -1) }, '➤'),
    ),
  );
}

function safetyPost(key: CaseKey, color: string): HTMLElement {
  const s = S();
  const c = m().social.cases[key];
  const solved = s.feed.solved.includes(key);
  const choose = (safe: boolean) => {
    if (safe) {
      if (!s.feed.solved.includes(key)) s.feed.solved.push(key);
      if (onceForever(`case.${key}`)) {
        celebrate();
        apply({ stars: 6 });
      } else sfx('ok');
    } else {
      sfx('bad');
      apply({ mood: -6 });
    }
    say({
      who: m().social.lessonTitle,
      icon: safe ? '✅' : '⚠️',
      text: `${safe ? c.safeMsg : c.riskyMsg}\n\n**${c.lesson}**`,
    });
    render();
  };
  return h(
    'article',
    { class: `fb-post scam${solved ? ' solved' : ''}` },
    h('div', { class: 'fb-author' }, h('span', { class: 'av', style: `background:${color}` }, Array.from(c.author)[1] ?? '?'), h('div', null, h('b', null, c.author), h('small', null, c.tag))),
    h('p', null, c.content),
    solved
      ? h('div', { class: 'lesson-card' }, h('b', null, m().social.lessonTitle), h('p', null, c.lesson))
      : h('div', { class: 'fb-actions col' }, h('button', { class: 'btn small green', onclick: () => choose(true) }, `🛡️ ${c.safe}`), h('button', { class: 'btn small ghost', onclick: () => choose(false) }, c.risky)),
  );
}

function oldPost(p: (typeof OLD)[number]): HTMLElement {
  const t = m().phone.feed;
  const s = S();
  const c = t.posts[p.key];
  const solved = s.feed.solved.includes(p.key);
  const liked = s.feed.liked.includes(p.key);
  const key = `old.${p.key}`;
  return h(
    'article',
    { class: `fb-post${p.scam ? ' scam' : ''}` },
    h('div', { class: 'fb-author' }, h('span', { class: 'av', style: `background:${p.color}` }, Array.from(c.author)[0]), h('div', null, h('b', null, c.author), h('small', null, c.time))),
    h('span', { class: `fb-tag${p.scam ? '' : ' ok'}` }, c.tag),
    h('p', null, c.content),
    p.scam
      ? solved
        ? h('span', { class: 'fb-done' }, t.reported)
        : h('div', { class: 'fb-actions' }, h('button', { class: 'btn small red', onclick: () => report(p.key) }, t.report), h('button', { class: 'btn small ghost', onclick: clickScam }, t.interact))
      : actionsRow(key, (liked ? 1 : 0) + 12, liked, false, () => like(p.key)),
    !p.scam && open.has(key) ? commentBox(key, false) : null,
  );
}

function feed() {
  const t = m().phone.feed;
  const so = m().social;
  const s = S();
  return {
    title: t.brand,
    body: [
      h('div', { class: 'fb-head' }, h('b', null, t.brand), h('small', null, t.tagline)),
      h(
        'div',
        { class: 'fb-tools' },
        h('button', { class: 'btn small orange', onclick: () => ((composing = { img: snapshot() }), render()) }, so.snapshot),
        h(
          'button',
          {
            class: 'btn small blue',
            onclick: async () => {
              const img = await pickImage();
              if (img) {
                composing = { img };
                render();
              }
            },
          },
          so.upload,
        ),
        h('button', { class: 'btn small ghost', onclick: rules }, t.rules),
      ),
      compose(),
      [...s.posts].reverse().map(myPost),
      CASES.slice(0, 2 + s.dayIndex * 2).map((c) => safetyPost(c.key, c.color)),
      OLD.map(oldPost),
    ],
  };
}

function report(key: OldKey): void {
  const s = S();
  if (!s.feed.solved.includes(key)) s.feed.solved.push(key);
  if (onceForever(`feed.${key}`)) {
    celebrate();
    apply({ stars: 5 });
  }
  toast(m().phone.feed.reportOk);
  render();
}

function clickScam(): void {
  sfx('bad');
  apply({ mood: -10 });
  toast(m().phone.feed.reportBad);
}

function like(key: OldKey): void {
  const s = S();
  const i = s.feed.liked.indexOf(key);
  if (i >= 0) s.feed.liked.splice(i, 1);
  else {
    s.feed.liked.push(key);
    apply({ mood: 2 }, { float: false });
  }
  sfx('pop');
  render();
}

function rules(): void {
  const t = m().phone.feed;
  closePhone();
  say({
    who: t.rulesTitle,
    icon: '🛡️',
    text: t.rulesText,
    choices: [
      {
        text: t.rulesClaim,
        run: () => {
          if (onceForever('feed.rules')) {
            celebrate();
            apply({ stars: 5, mood: 5 });
          }
          openPhone('feed');
        },
      },
    ],
  });
}

export function initFeed(): void {
  registerTab('feed', feed);
}
